import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format, addDays, parse } from "date-fns";
import { formatSiteTzLabel } from "@/shared/lib/siteTime";
import { ShiftType, ShiftTypeLabels } from "@/entities/shift-template/model/types";
import type { CreateShiftDefaults } from "./createShiftContext";
import { DAY_OF_WEEK_NAMES, DayOfWeekLabels } from "@/entities/shift/model/types";
import type { DayOfWeek } from "@/entities/shift/model/types";
import { useCreateShift } from "@/entities/shift/model/queries";
import { useShiftCategories } from "@/entities/shift-category/model/queries";
import { useClientLookup } from "@/entities/client/model/queries";
import { useSiteLookup, useSite } from "@/entities/site/model/queries";
import { assignableSites } from "@/entities/site/model/assignable";
import { useGuardLookup } from "@/entities/employee/model/queries";
import { PersonOption, personSearchValue } from "@/entities/employee";
import { useCredentialValidity } from "@/entities/credential";
import { useResolvedPayRates } from "@/entities/pay-rate";
import { usePermissions } from "@/entities/session/hooks/usePermissions";
import { PERMISSIONS } from "@/shared/config/permissions";
import { getApiErrorMessage } from "@/shared/api/getApiErrorMessage";
import { formatCurrency } from "@/shared/lib/formatCurrency";
import { useGuardOvertimeHints } from "./useGuardOvertimeHints";
import { GuardOtBadge } from "./GuardOtBadge";
import { ShiftRatesStep } from "./ShiftRatesStep";
import { endDateOffsetLabel, openSpotCount } from "./shiftFormRules";
import {
  buildReviewRates,
  ratesLoadErrorMessage,
  shiftHours,
  shiftOccurrences,
  toRateGuards,
  type ReviewRates,
} from "./shiftRates";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Badge } from "@/shared/components/ui/badge";
import { RoleBadge } from "@/shared/components/ui/RoleBadge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { Check, ChevronDown, ChevronUp, ChevronsUpDown, Loader2, Palette, X } from "lucide-react";
import { cn } from "@/shared/lib/utils";

const RECURRENCE_OPTIONS: { label: string; weeks: number }[] = [
  { label: "1 week", weeks: 1 },
  { label: "2 weeks", weeks: 2 },
  { label: "3 weeks", weeks: 3 },
  { label: "4 weeks", weeks: 4 },
  { label: "5 weeks", weeks: 5 },
  { label: "6 weeks", weeks: 6 },
  { label: "2 months", weeks: 9 },
  { label: "3 months", weeks: 13 },
  { label: "4 months", weeks: 17 },
  { label: "5 months", weeks: 22 },
  { label: "6 months", weeks: 26 },
];

const SHIFT_COLORS = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308",
  "#84cc16", "#22c55e", "#10b981", "#14b8a6",
  "#06b6d4", "#0ea5e9", "#3b82f6", "#6366f1",
  "#8b5cf6", "#a855f7", "#d946ef", "#ec4899",
  "#f43f5e", "#78716c", "#64748b", "#1e293b",
];

const createShiftSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  shiftType: z.string(),
  startDate: z.string().min(1, "Start Date is required"),
  endDate: z.string().min(1, "End Date is required"),
  startTime: z.string().min(1, "Start time is required"),
  endTime: z.string().min(1, "End time is required"),
  siteId: z.number({ error: "Site is required" }).min(1, "Site is required"),
  numberOfGuards: z.number().min(1, "At least 1 guard required"),
  shiftColor: z.string().nullable().optional(),
  categoryId: z.number().nullable().optional(),
  note: z.string().nullable().optional(),
  guardUserIds: z.array(z.number()).optional(),
  recurrenceDays: z.array(z.enum(DAY_OF_WEEK_NAMES)).optional(),
  recurrenceWeeks: z.number().min(1).max(26).optional(),
});

type CreateShiftFormData = z.infer<typeof createShiftSchema>;

type WizardStep = "details" | "rates" | "review";

const isRateFieldError = (propertyName: unknown): boolean =>
  typeof propertyName === "string" && /^guardrates/i.test(propertyName);

const MORE_OPTION_FIELDS: (keyof CreateShiftFormData)[] = [
  "title",
  "categoryId",
  "shiftColor",
  "note",
  "recurrenceDays",
  "recurrenceWeeks",
];

interface CreateShiftModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  defaults?: CreateShiftDefaults;
}

export function CreateShiftModal({
  open,
  onOpenChange,
  onSuccess,
  defaults,
}: CreateShiftModalProps) {
  const {
    date: defaultDate,
    template: defaultTemplate,
    clientId: defaultClientId,
    siteId: defaultSiteId,
    guardId: defaultGuardId,
    categoryId: defaultCategoryId,
    shiftType: defaultShiftType,
    endDate: defaultEndDate,
    startTime: defaultStartTime,
    endTime: defaultEndTime,
    numberOfGuards: defaultNumberOfGuards,
  } = defaults ?? {};
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>();
  const [step, setStep] = useState<WizardStep>("details");
  const [customRates, setCustomRates] = useState<Record<number, number>>({});
  const [showMore, setShowMore] = useState(false);
  const { hasPermission } = usePermissions();
  const canViewFinance = hasPermission(PERMISSIONS.FinanceView);
  const canSetPayRates = hasPermission(PERMISSIONS.PayrollRates);
  const [titleEdited, setTitleEdited] = useState(false);
  const createMutation = useCreateShift();
  const { data: categories } = useShiftCategories();
  const { data: clients, isLoading: isLoadingClients } = useClientLookup();
  const { data: sites, isLoading: isLoadingSites } = useSiteLookup(selectedClientId);
  const { data: templateSite } = useSite(defaultTemplate?.siteId ?? 0);

  const form = useForm<CreateShiftFormData>({
    resolver: zodResolver(createShiftSchema),
    defaultValues: {
      title: "",
      shiftType: defaultShiftType ?? ShiftType.Standard,
      startDate: defaultDate ? format(defaultDate, "yyyy-MM-dd") : "",
      endDate: defaultDate ? format(defaultDate, "yyyy-MM-dd") : "",
      startTime: "08:00",
      endTime: "16:00",
      siteId: undefined as unknown as number,
      numberOfGuards: 1,
      shiftColor: null,
      categoryId: defaultCategoryId ?? null,
      note: "",
      guardUserIds: [],
      recurrenceDays: [],
      recurrenceWeeks: 1,
    },
  });

  const watchShiftType = form.watch("shiftType");
  const { data: guards } = useGuardLookup(
    watchShiftType === ShiftType.Open ? undefined : selectedClientId,
  );

  // Standard shifts re-scope the roster to the client's authorized guards; drop any
  // now-unauthorized picks so switching from Open can't leave an unsaveable selection.
  useEffect(() => {
    if (watchShiftType !== ShiftType.Standard || !guards) return;
    const allowed = new Set(guards.map((g) => g.id));
    const current = form.getValues("guardUserIds") ?? [];
    const pruned = current.filter((id) => allowed.has(id));
    if (pruned.length !== current.length) {
      form.setValue("guardUserIds", pruned);
    }
  }, [watchShiftType, guards, form]);

  useEffect(() => {
    if (defaultDate && open) {
      form.setValue("startDate", format(defaultDate, "yyyy-MM-dd"));
      form.setValue("endDate", format(defaultDate, "yyyy-MM-dd"));
    }
  }, [defaultDate, open, form]);

  // Pre-fill form fields from template when modal opens
  useEffect(() => {
    if (!defaultTemplate || !open) return;
    form.reset({
      title: defaultTemplate.title,
      shiftType: ShiftType.Standard,
      startDate: defaultDate ? format(defaultDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
      endDate: defaultDate ? format(defaultDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
      startTime: defaultTemplate.startTime.slice(0, 5),
      endTime: defaultTemplate.endTime.slice(0, 5),
      siteId: undefined as unknown as number,
      numberOfGuards: 1,
      shiftColor: null,
      categoryId: defaultTemplate.categoryId,
      note: defaultTemplate.note || "",
      guardUserIds: defaultTemplate.guardUserIds ?? [],
      recurrenceDays: defaultTemplate.recurrenceDays ?? [],
      recurrenceWeeks: defaultTemplate.recurrenceWeeks ?? 1,
    });
  }, [defaultTemplate, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once the template's site resolves, set the client and site fields
  useEffect(() => {
    if (!defaultTemplate?.siteId || !templateSite || !open) return;
    setSelectedClientId(templateSite.clientId);
    form.setValue("siteId", defaultTemplate.siteId);
  }, [defaultTemplate?.siteId, templateSite, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-populate client from context (e.g. client profile page)
  useEffect(() => {
    if (!defaultClientId || !open) return;
    setSelectedClientId(defaultClientId);
  }, [defaultClientId, open]);

  // Auto-populate site once client's sites are loaded
  useEffect(() => {
    if (!defaultSiteId || !sites || sites.length === 0 || !open) return;
    if (!form.getValues("siteId")) {
      form.setValue("siteId", defaultSiteId);
    }
  }, [defaultSiteId, sites, open, form]);

  // Auto-populate guard from context (e.g. employee profile page)
  useEffect(() => {
    if (!defaultGuardId || !open) return;
    if ((form.getValues("guardUserIds") ?? []).length === 0) {
      form.setValue("guardUserIds", [defaultGuardId]);
    }
  }, [defaultGuardId, open, form]);

  // Auto-populate the window and headcount from context (e.g. an accepted coverage request)
  useEffect(() => {
    if (!open) return;
    if (defaultStartTime) form.setValue("startTime", defaultStartTime);
    if (defaultEndTime) form.setValue("endTime", defaultEndTime);
    if (defaultEndDate) form.setValue("endDate", defaultEndDate);
    if (defaultNumberOfGuards) form.setValue("numberOfGuards", defaultNumberOfGuards);
  }, [open, defaultStartTime, defaultEndTime, defaultEndDate, defaultNumberOfGuards, form]);

  // Smart Auto-fill End Date for Overnight Shifts
  const watchStartDate = form.watch("startDate");
  const watchStartTime = form.watch("startTime");
  const watchEndTime = form.watch("endTime");
  const watchSiteId = form.watch("siteId");
  const { data: selectedSite } = useSite(watchSiteId ?? 0);
  // Parked sites are not choosable, but one that arrived prefilled (the client shifts tab's site filter,
  // a template) has to stay in the list, or the select renders blank while the form still holds its id.
  const prefilledSiteId = defaultSiteId ?? defaultTemplate?.siteId;
  const choosableSites = assignableSites(sites, prefilledSiteId);
  const parkedSelectedSite = sites?.find((s) => s.siteId === watchSiteId && !s.isActive);
  const autoBumpedEndDate = useRef<string | null>(null);

  // The bump has to be undoable, not just doable. Setting a start time later than the
  // end time is an ordinary step on the way to a same-day shift — 08:00-16:00 becoming
  // 18:00-22:00 passes through 18:00-16:00 — and a bump that only ever adds a day turns
  // that keystroke into an overnight shift the user never asked for and never saw.
  // Undoing only the exact value we wrote leaves a deliberately multi-day end date alone.
  useEffect(() => {
    if (!watchStartDate || !watchStartTime || !watchEndTime) return;

    try {
      const parsedStart = parse(watchStartTime, "HH:mm", new Date());
      const parsedEnd = parse(watchEndTime, "HH:mm", new Date());
      const currentEndDate = form.getValues("endDate");
      const nextDay = format(
        addDays(parse(watchStartDate, "yyyy-MM-dd", new Date()), 1),
        "yyyy-MM-dd",
      );

      if (parsedEnd < parsedStart) {
        if (currentEndDate === watchStartDate) {
          form.setValue("endDate", nextDay);
          autoBumpedEndDate.current = nextDay;
        }
      } else if (autoBumpedEndDate.current === currentEndDate) {
        form.setValue("endDate", watchStartDate);
        autoBumpedEndDate.current = null;
      }
    } catch {
      // Ignore parse errors from temporary partial typing
    }
  }, [watchStartDate, watchStartTime, watchEndTime, form]);

  // Auto-default the title from site + start unless the user has edited it
  useEffect(() => {
    if (defaultTemplate || titleEdited) return;
    const site = sites?.find((s) => s.siteId === watchSiteId);
    if (!site) {
      form.setValue("title", "New shift");
      return;
    }
    let dateLabel = "";
    try {
      if (watchStartDate) {
        dateLabel = format(parse(watchStartDate, "yyyy-MM-dd", new Date()), "MMM d");
      }
    } catch {
      dateLabel = "";
    }
    const suffix = [dateLabel, watchStartTime].filter(Boolean).join(" ");
    form.setValue("title", suffix ? `${site.siteName} — ${suffix}` : site.siteName);
  }, [defaultTemplate, titleEdited, watchSiteId, watchStartDate, watchStartTime, sites, form]);

  const onSubmit = async (data: CreateShiftFormData, openForClaim: boolean) => {
    try {
      const startTime = `${data.startDate}T${data.startTime}:00`;
      const endTime = `${data.endDate}T${data.endTime}:00`;
      const guardRates = (data.guardUserIds ?? [])
        .filter((id) => customRates[id] !== undefined)
        .map((id) => ({ guardUserId: id, payRate: customRates[id] }));

      await createMutation.mutateAsync({
        title: data.title,
        shiftType: data.shiftType as ShiftType,
        startTime,
        endTime,
        siteId: data.siteId,
        numberOfGuards: data.numberOfGuards,
        shiftColor: data.shiftColor,
        categoryId: data.categoryId,
        note: data.note || null,
        guardUserIds:
          data.guardUserIds && data.guardUserIds.length > 0
            ? data.guardUserIds
            : undefined,
        recurrenceDays:
          data.recurrenceDays && data.recurrenceDays.length > 0
            ? data.recurrenceDays
            : undefined,
        recurrenceWeeks:
          data.recurrenceDays && data.recurrenceDays.length > 0
            ? data.recurrenceWeeks
            : undefined,
        openForClaim,
        guardRates: guardRates.length > 0 ? guardRates : undefined,
      });

      form.reset();
      setTitleEdited(false);
      setStep("details");
      setCustomRates({});
      onOpenChange(false);
      onSuccess?.();
    } catch (error: unknown) {
      const axiosError = error as { response?: { status: number; data?: unknown } };
      const responseData = axiosError.response?.data;
      if (axiosError.response?.status === 422 && Array.isArray(responseData)) {
        const validationErrors = (
          responseData as Array<{ propertyName: string; message: string }>
        ).filter((err) => typeof err.propertyName === "string" && !isRateFieldError(err.propertyName));
        const erroredFields = validationErrors.map(
          (err) => err.propertyName.charAt(0).toLowerCase() + err.propertyName.slice(1)
        );
        erroredFields.forEach((fieldName, i) => {
          form.setError(fieldName as keyof CreateShiftFormData, {
            type: "manual",
            message: validationErrors[i].message,
          });
        });
        if (erroredFields.length > 0) {
          setStep("details");
        }
        if (erroredFields.some((f) => MORE_OPTION_FIELDS.includes(f as keyof CreateShiftFormData))) {
          setShowMore(true);
        }
      }
      toast.error(await getApiErrorMessage(error));
    }
  };

  const handleReview = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      setStep(hasRatesStep ? "rates" : "review");
      return;
    }
    const erroredFields = Object.keys(form.formState.errors);
    if (erroredFields.some((f) => MORE_OPTION_FIELDS.includes(f as keyof CreateShiftFormData))) {
      setShowMore(true);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
      setSelectedClientId(undefined);
      setStep("details");
      setCustomRates({});
      setShowMore(false);
      setTitleEdited(false);
      autoBumpedEndDate.current = null;
    }
    onOpenChange(newOpen);
  };

  const isPending = createMutation.isPending;
  const selectedGuards = form.watch("guardUserIds") ?? [];
  const hasRatesStep = canViewFinance && selectedGuards.length > 0;
  const ratesQuery = useResolvedPayRates(
    watchSiteId ?? 0,
    selectedGuards,
    open && hasRatesStep && step !== "details",
  );
  const stepBeforeReview: WizardStep = hasRatesStep ? "rates" : "details";
  const contentStep: WizardStep = step === "review" ? stepBeforeReview : step;

  // The count is a target that ratchets upward with the roster and never follows it
  // back down: assigning a fourth guard raises it to four, and removing one leaves it
  // at four so the freed slot becomes an open spot the admin deliberately kept.
  useEffect(() => {
    const current = form.getValues("numberOfGuards");
    if (selectedGuards.length > current) {
      form.setValue("numberOfGuards", selectedGuards.length);
    }
  }, [selectedGuards.length, form]);
  const recurrenceDays = form.watch("recurrenceDays") ?? [];
  const endDate = form.watch("endDate");
  const candidateGuardIds = (guards ?? []).map((g) => g.id);
  const { data: validity } = useCredentialValidity(candidateGuardIds, endDate);
  const validityById = new Map((validity ?? []).map((v) => [v.userId, v]));
  const otHints = useGuardOvertimeHints({
    startDate: watchStartDate,
    startTime: watchStartTime,
    endDate,
    endTime: watchEndTime,
    recurrenceDays,
  });
  const invalidSelected = selectedGuards.filter((id) => validityById.get(id)?.isValid === false);
  const invalidSelectedMessages = invalidSelected.flatMap((id) => {
    const guard = guards?.find((g) => g.id === id);
    const name = guard ? `${guard.firstName} ${guard.lastName}` : `Guard ${id}`;
    return (validityById.get(id)?.problems ?? []).map((p) => `${name} — ${p}`);
  });

  const rateGuards = toRateGuards(selectedGuards, guards);
  const reviewRates: ReviewRates | undefined =
    step === "review" && hasRatesStep && ratesQuery.data && !ratesQuery.isError
      ? buildReviewRates({
          guards: rateGuards,
          customRates,
          resolved: ratesQuery.data,
          hours:
            shiftHours(watchStartDate, watchStartTime, endDate, watchEndTime) *
            shiftOccurrences(recurrenceDays, form.watch("recurrenceWeeks")),
        })
      : undefined;
  const stepLabels = hasRatesStep ? ["Details", "Pay rates", "Review"] : ["Details", "Review"];

  const handleRatesNext = () => {
    if (ratesQuery.isError) setCustomRates({});
    setStep("review");
  };

  const handleCustomRateChange = (guardUserId: number, rate: number | null) => {
    setCustomRates((current) => {
      const next = { ...current };
      if (rate === null) {
        delete next[guardUserId];
      } else {
        next[guardUserId] = rate;
      }
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Shift</DialogTitle>
          <DialogDescription>
            {contentStep === "rates"
              ? "Set each guard's hourly pay for this shift."
              : "Create a new shift. You can optionally set up recurring days."}
          </DialogDescription>
          {canViewFinance && (
            <ShiftWizardSteps
              labels={stepLabels}
              current={step === "details" ? 0 : step === "rates" ? 1 : stepLabels.length - 1}
            />
          )}
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleReview();
            }}
            className={cn("space-y-4", contentStep !== "details" && "hidden")}
          >
            <div className="grid grid-cols-2 gap-4">
              <FormItem>
                <FormLabel>Client *</FormLabel>
                <Select
                  value={selectedClientId ? String(selectedClientId) : ""}
                  disabled={isLoadingClients}
                  onValueChange={(v) => {
                    setSelectedClientId(Number(v));
                    form.setValue("siteId", undefined as unknown as number);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isLoadingClients ? "Loading clients..." : "Select a client"} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients?.map((client) => (
                      <SelectItem key={client.id} value={String(client.id)}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>

              <FormField
                control={form.control}
                name="siteId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Post Site *
                      {!selectedClientId && (
                        <span className="ml-1 text-xs font-normal text-muted-foreground">(select client first)</span>
                      )}
                    </FormLabel>
                    <Select
                      key={selectedClientId ?? "no-client"}
                      value={field.value ? String(field.value) : ""}
                      onValueChange={(v) => {
                        if (!v) return;
                        field.onChange(Number(v));
                      }}
                      disabled={!selectedClientId || isLoadingSites}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={
                            isLoadingSites ? "Loading sites..." :
                            selectedClientId ? "Select a site" : "Select client first"
                          } />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {choosableSites.length > 0 ? (
                          choosableSites.map((site) => (
                            // A parked site is only here because it was prefilled. It is listed so the
                            // select can say what it holds, and disabled so it cannot be picked again.
                            <SelectItem
                              key={site.siteId}
                              value={String(site.siteId)}
                              disabled={!site.isActive}
                            >
                              {site.isActive ? site.siteName : `${site.siteName} (not active)`}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="__disabled" disabled>
                            No sites found
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {parkedSelectedSite && (
                      <p role="alert" className="text-sm text-amber-600 dark:text-amber-500">
                        {parkedSelectedSite.siteName} is not active, so shifts can't be added to it. Choose another
                        site, or switch it on from the client's Sites tab.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Time *</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => {
                  const offsetLabel = endDateOffsetLabel(watchStartDate, field.value);
                  return (
                    <FormItem>
                      <FormLabel>End Date *</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      {offsetLabel && (
                        <p className="text-xs font-medium text-amber-600 dark:text-amber-500">
                          {offsetLabel}
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Time *</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {selectedSite?.timeZone && (
              <p className="text-xs text-muted-foreground">
                Times are in the site&apos;s timezone — {formatSiteTzLabel(selectedSite.timeZone)}
              </p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="shiftType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Shift Type *</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(v) => field.onChange(v as ShiftType)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(ShiftTypeLabels).map(([key, label]) => (
                          <SelectItem key={key} value={key}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {field.value === ShiftType.Open
                        ? "Any guard in your organization can claim an open spot."
                        : "Only guards authorized for this client can claim."}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="numberOfGuards"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Number of Guards *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Guard Assignment */}
            <FormField
              control={form.control}
              name="guardUserIds"
              render={() => (
                <FormItem>
                  <FormLabel>Assign Guards</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between font-normal"
                      >
                        {selectedGuards.length > 0
                          ? `${selectedGuards.length} guard${selectedGuards.length > 1 ? "s" : ""} selected`
                          : "Search and select guards..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search guards..." />
                        <CommandList>
                          <CommandEmpty>No guards found.</CommandEmpty>
                          <CommandGroup>
                            {guards?.map((guard) => {
                              const isSelected = selectedGuards.includes(guard.id);
                              return (
                                <CommandItem
                                  key={guard.id}
                                  value={personSearchValue(guard)}
                                  onSelect={() => {
                                    const current = form.getValues("guardUserIds") ?? [];
                                    form.setValue(
                                      "guardUserIds",
                                      isSelected
                                        ? current.filter((id) => id !== guard.id)
                                        : [...current, guard.id]
                                    );
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      isSelected ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <PersonOption person={guard} />
                                  <RoleBadge employeeType={guard.employeeType} className="ml-2" />
                                  {validityById.get(guard.id)?.isValid === false && (
                                    <Badge tone="warn" className="ml-2">credential</Badge>
                                  )}
                                  <GuardOtBadge hint={otHints.get(guard.id)} />
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {selectedGuards.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {selectedGuards.map((id) => {
                        const guard = guards?.find((g) => g.id === id);
                        return guard ? (
                          <Badge key={id} variant="secondary" className="text-xs gap-1">
                            {guard.firstName} {guard.lastName}
                            <button
                              type="button"
                              onClick={() =>
                                form.setValue(
                                  "guardUserIds",
                                  selectedGuards.filter((gid) => gid !== id)
                                )
                              }
                              className="ml-1 rounded-full hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  {invalidSelectedMessages.length > 0 && (
                    <ul className="mt-1 text-xs text-[var(--bad)]">
                      {invalidSelectedMessages.map((m, i) => (
                        <li key={i}>{m}</li>
                      ))}
                    </ul>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4 border-t pt-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto px-0 font-medium text-muted-foreground hover:bg-transparent hover:text-foreground"
                onClick={() => setShowMore((v) => !v)}
              >
                More options
                {showMore ? (
                  <ChevronUp className="ml-1 h-4 w-4" />
                ) : (
                  <ChevronDown className="ml-1 h-4 w-4" />
                )}
              </Button>

              {showMore && (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Morning Guard Shift"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setTitleEdited(true);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="categoryId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <Select
                          value={field.value ? String(field.value) : "none"}
                          onValueChange={(v) =>
                            field.onChange(v === "none" ? null : Number(v))
                          }
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {categories
                              ?.filter((c) => c.isActive)
                              .map((cat) => (
                                <SelectItem key={cat.id} value={String(cat.id)}>
                                  {cat.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Color Picker */}
                  <FormField
                    control={form.control}
                    name="shiftColor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Shift Color</FormLabel>
                        <div className="flex items-center gap-3">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                className="h-9 gap-2"
                              >
                                {field.value ? (
                                  <span
                                    className="h-4 w-4 rounded-sm border"
                                    style={{ backgroundColor: field.value }}
                                  />
                                ) : (
                                  <Palette className="h-4 w-4" />
                                )}
                                {field.value ?? "Pick a color"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[272px] p-3" align="start">
                              <div className="space-y-3">
                                <div className="grid grid-cols-5 gap-2">
                                  {SHIFT_COLORS.map((color) => (
                                    <button
                                      key={color}
                                      type="button"
                                      className={`h-8 w-8 rounded-md border-2 transition-all hover:scale-110 ${field.value === color
                                        ? "border-foreground ring-2 ring-foreground/20"
                                        : "border-transparent"
                                        }`}
                                      style={{ backgroundColor: color }}
                                      onClick={() => field.onChange(color)}
                                    />
                                  ))}
                                </div>
                                <div className="flex items-center gap-2 border-t pt-3">
                                  <label className="text-xs text-muted-foreground">Custom:</label>
                                  <input
                                    type="color"
                                    value={field.value ?? "#3b82f6"}
                                    onChange={(e) => field.onChange(e.target.value)}
                                    className="h-8 w-8 cursor-pointer rounded border-0 bg-transparent p-0"
                                  />
                                  {field.value && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="ml-auto h-7 text-xs"
                                      onClick={() => field.onChange(null)}
                                    >
                                      Clear
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Recurrence */}
                  <div className="space-y-2">
                    <FormLabel>Repeat on Days</FormLabel>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(DayOfWeekLabels).map(([key, label]) => {
                        const dayValue = key as DayOfWeek;
                        const isSelected = recurrenceDays.includes(dayValue);
                        return (
                          <Button
                            key={key}
                            type="button"
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            className="h-8 w-12"
                            onClick={() => {
                              const current =
                                form.getValues("recurrenceDays") ?? [];
                              form.setValue(
                                "recurrenceDays",
                                isSelected
                                  ? current.filter((d) => d !== dayValue)
                                  : [...current, dayValue]
                              );
                            }}
                          >
                            {label}
                          </Button>
                        );
                      })}
                    </div>

                    {recurrenceDays.length > 0 && (
                      <FormField
                        control={form.control}
                        name="recurrenceWeeks"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Repeat for</FormLabel>
                            <Select
                              value={field.value ? String(field.value) : "1"}
                              onValueChange={(v) => field.onChange(Number(v))}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {RECURRENCE_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.weeks} value={String(opt.weeks)}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name="note"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Note</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Additional notes..."
                            className="resize-none"
                            rows={2}
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleReview}
                disabled={isPending || invalidSelected.length > 0 || parkedSelectedSite !== undefined}
              >
                {hasRatesStep ? "Next" : "Review"}
              </Button>
            </DialogFooter>
          </form>
        </Form>

        {contentStep === "rates" && (
          <ShiftRatesStep
            guards={rateGuards}
            resolved={ratesQuery.data}
            isLoading={ratesQuery.isLoading}
            errorMessage={ratesLoadErrorMessage(ratesQuery.error)}
            onRetry={() => void ratesQuery.refetch()}
            customRates={customRates}
            onCustomRateChange={handleCustomRateChange}
            canManage={canSetPayRates}
            onBack={() => setStep("details")}
            onNext={handleRatesNext}
          />
        )}

        {step === "review" && (
          <ShiftSummaryDialog
            open
            onBack={() => setStep(stepBeforeReview)}
            onSave={form.handleSubmit((d) => onSubmit(d, false))}
            onPublish={form.handleSubmit((d) => onSubmit(d, true))}
            isSubmitting={isPending}
            values={form.getValues()}
            clientName={clients?.find((c) => c.id === selectedClientId)?.name}
            siteName={sites?.find((s) => s.siteId === form.getValues("siteId"))?.siteName}
            categoryName={
              categories?.find((c) => c.id === form.getValues("categoryId"))?.name
            }
            guardNames={
              (form.getValues("guardUserIds") ?? [])
                .map((id) => {
                  const g = guards?.find((x) => x.id === id);
                  return g ? `${g.firstName} ${g.lastName}` : null;
                })
                .filter((n): n is string => n !== null)
            }
            rates={reviewRates}
            stepLabels={canViewFinance ? stepLabels : undefined}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ShiftSummaryDialogProps {
  open: boolean;
  onBack: () => void;
  onSave: () => void;
  onPublish: () => void;
  isSubmitting: boolean;
  values: CreateShiftFormData;
  clientName?: string;
  siteName?: string;
  categoryName?: string;
  guardNames: string[];
  rates?: ReviewRates;
  stepLabels?: string[];
}

function ShiftSummaryDialog({
  open,
  onBack,
  onSave,
  onPublish,
  isSubmitting,
  values,
  clientName,
  siteName,
  categoryName,
  guardNames,
  rates,
  stepLabels,
}: ShiftSummaryDialogProps) {
  const startLabel = `${values.startDate} ${values.startTime}`;
  const endLabel = `${values.endDate} ${values.endTime}`;
  // Counted from the selected ids, never from the resolved names: a name is dropped
  // when its guard is missing from the loaded roster, which would over-report open
  // spots and put the Publish button back on a shift that is already full.
  const openSpots = openSpotCount(values.numberOfGuards, values.guardUserIds?.length ?? 0);
  const overnightLabel = endDateOffsetLabel(values.startDate, values.endDate);
  const recurrenceLabel = (values.recurrenceDays ?? []).length > 0
    ? `${(values.recurrenceDays ?? [])
        .map((d) => DayOfWeekLabels[d])
        .join(", ")} for ${
          RECURRENCE_OPTIONS.find((o) => o.weeks === values.recurrenceWeeks)?.label
            ?? `${values.recurrenceWeeks} weeks`
        }`
    : "Does not repeat";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onBack(); }}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirm Shift Details</DialogTitle>
          <DialogDescription>
            Review the shift below before creating it.
          </DialogDescription>
          {stepLabels && (
            <ShiftWizardSteps labels={stepLabels} current={stepLabels.length - 1} />
          )}
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <SummaryRow label="Title" value={values.title} />
          <SummaryRow label="Type" value={ShiftTypeLabels[values.shiftType as ShiftType]} />
          <SummaryRow label="Client" value={clientName ?? "—"} />
          <SummaryRow label="Site" value={siteName ?? "—"} />
          <SummaryRow label="Start" value={startLabel} />
          <SummaryRow label="End" value={endLabel} />
          {overnightLabel && <SummaryRow label="Spans" value={overnightLabel} />}
          <SummaryRow
            label="Number of Guards"
            value={
              openSpots > 0
                ? `${values.numberOfGuards} (${openSpots} open)`
                : String(values.numberOfGuards)
            }
          />
          {categoryName && <SummaryRow label="Category" value={categoryName} />}
          {rates ? (
            <ReviewRatesBlock rates={rates} />
          ) : (
            guardNames.length > 0 && (
              <SummaryRow label="Assigned Guards" value={guardNames.join(", ")} />
            )
          )}
          <SummaryRow label="Recurrence" value={recurrenceLabel} />
          {values.note && <SummaryRow label="Note" value={values.note} />}
        </div>

        <p className="text-xs text-muted-foreground">
          {openSpots > 0
            ? "Publish opens the unfilled spots for guards to self-claim."
            : `All ${values.numberOfGuards} spot${values.numberOfGuards > 1 ? "s are" : " is"} filled, so there is nothing to self-claim. Raise the guard count to open one.`}
        </p>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onBack} disabled={isSubmitting}>
            Back
          </Button>
          <Button
            type="button"
            variant={openSpots > 0 ? "outline" : "default"}
            onClick={onSave}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {openSpots > 0 ? "Save (hold spots)" : "Create shift"}
          </Button>
          {openSpots > 0 && (
            <Button type="button" onClick={onPublish} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Publish for self-claim
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShiftWizardSteps({ labels, current }: { labels: string[]; current: number }) {
  return (
    <ol aria-label="Progress" className="flex items-center gap-2 text-xs text-muted-foreground">
      {labels.map((label, index) => (
        <li
          key={label}
          aria-current={index === current ? "step" : undefined}
          className="flex items-center gap-2"
        >
          {index > 0 && <span aria-hidden="true">·</span>}
          <span className={cn(index === current && "font-semibold text-foreground")}>{label}</span>
        </li>
      ))}
    </ol>
  );
}

function ReviewRatesBlock({ rates }: { rates: ReviewRates }) {
  const { guards, billRate, estimate } = rates;
  return (
    <div className="space-y-2 border-b pb-2">
      <span className="text-muted-foreground">Assigned Guards</span>
      <ul className="space-y-1">
        {guards.map((guard) => (
          <li key={guard.id} className="flex items-center justify-between gap-4">
            <span className="font-medium break-words">{guard.name}</span>
            <span className="flex items-center gap-2 tabular-nums">
              {guard.isCustom && <Badge tone="info">Custom</Badge>}
              {guard.rate === null ? "No rate" : `${formatCurrency(guard.rate)} /h`}
            </span>
          </li>
        ))}
      </ul>
      {billRate !== null && (
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">Bill rate</span>
          <span className="font-medium tabular-nums">{formatCurrency(billRate)} /h</span>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Estimated pay {formatCurrency(estimate.pay)}
        {estimate.billing !== null && ` · Estimated billing ${formatCurrency(estimate.billing)}`}
      </p>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b pb-2 last:border-0 last:pb-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right break-words max-w-[60%]">{value}</span>
    </div>
  );
}
